import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtCLP } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BusinessCard from "../components/BusinessCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "../components/ui/sheet";
import { useAuth } from "../lib/auth";
import { SlidersHorizontal, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [regions, setRegions] = useState([]);
  const [comunas, setComunas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceMax, setPriceMax] = useState(parseInt(params.get("price_max") || "300000000", 10));

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    region_id: params.get("region_id") || "",
    comuna_id: params.get("comuna_id") || "",
    category_id: params.get("category_id") || "",
    price_min: params.get("price_min") || "",
    price_max: params.get("price_max") || "",
    sort: params.get("sort") || "recent",
  }), [params]);

  useEffect(() => {
    api.get("/regions").then((r) => setRegions(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    if (filters.region_id) {
      api.get(`/regions/${filters.region_id}/comunas`).then((r) => setComunas(r.data));
    } else {
      setComunas([]);
    }
  }, [filters.region_id]);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/businesses?${q.toString()}`).then((r) => { setData(r.data); setLoading(false); });
  }, [filters]);

  useEffect(() => {
    if (user?.role === "investor") {
      api.get("/favorites").then((r) => setFavs(r.data.map((b) => b.id))).catch(() => {});
    }
  }, [user]);

  const setF = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    if (k === "region_id") np.delete("comuna_id");
    setParams(np);
  };

  const clear = () => setParams(new URLSearchParams());

  const toggleFav = async (biz) => {
    if (!user) { toast.error("Debes iniciar sesión para guardar favoritos"); return; }
    if (user.role !== "investor") { toast.error("Sólo inversionistas pueden guardar favoritos"); return; }
    if (favs.includes(biz.id)) {
      await api.delete(`/favorites/${biz.id}`);
      setFavs(favs.filter((id) => id !== biz.id));
    } else {
      await api.post(`/favorites/${biz.id}`);
      setFavs([...favs, biz.id]);
      toast.success("Negocio guardado en tus favoritos");
    }
  };

  const FilterPanel = (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold mb-2">Búsqueda</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="filter-search"
            value={filters.q}
            onChange={(e) => setF("q", e.target.value)}
            placeholder="Cafetería, gimnasio..."
            className="pl-9"
          />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold mb-2">Región</p>
        <Select value={filters.region_id} onValueChange={(v) => setF("region_id", v)}>
          <SelectTrigger data-testid="filter-region"><SelectValue placeholder="Todas las regiones" /></SelectTrigger>
          <SelectContent>
            {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {comunas.length > 0 && (
        <div>
          <p className="text-xs font-semibold mb-2">Comuna</p>
          <Select value={filters.comuna_id} onValueChange={(v) => setF("comuna_id", v)}>
            <SelectTrigger data-testid="filter-comuna"><SelectValue placeholder="Todas las comunas" /></SelectTrigger>
            <SelectContent>
              {comunas.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold mb-2">Categoría</p>
        <Select value={filters.category_id} onValueChange={(v) => setF("category_id", v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Todas las categorías" /></SelectTrigger>
          <SelectContent>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs font-semibold mb-2">Precio máximo</p>
        <Slider
          data-testid="filter-price-slider"
          value={[priceMax]}
          min={5000000}
          max={500000000}
          step={5000000}
          onValueChange={(v) => setPriceMax(v[0])}
          onValueCommit={(v) => setF("price_max", String(v[0]))}
        />
        <p className="text-xs text-slate-500 mt-2">Hasta {fmtCLP(priceMax)}</p>
      </div>
      <Button variant="outline" className="w-full" onClick={clear} data-testid="clear-filters">
        <X className="w-4 h-4 mr-2" /> Limpiar filtros
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <span className="overline text-emerald-700">Marketplace</span>
            <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">Negocios funcionando en Chile</h1>
            <p className="text-slate-500 mt-1 text-sm">{data.total} oportunidades disponibles</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filters.sort} onValueChange={(v) => setF("sort", v)}>
              <SelectTrigger className="w-44" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Más recientes</SelectItem>
                <SelectItem value="price_asc">Precio ↑</SelectItem>
                <SelectItem value="price_desc">Precio ↓</SelectItem>
                <SelectItem value="profit">Mayor utilidad</SelectItem>
              </SelectContent>
            </Select>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden" data-testid="mobile-filters-btn">
                  <SlidersHorizontal className="w-4 h-4 mr-2" /> Filtros
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader><SheetTitle>Filtros</SheetTitle></SheetHeader>
                <div className="mt-6">{FilterPanel}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="hidden lg:block lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-6 sticky top-24 self-start">
            {FilterPanel}
          </aside>

          <main className="lg:col-span-9">
            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white border border-zinc-200 rounded-lg p-6 animate-pulse h-80" />
                ))}
              </div>
            ) : data.items.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center" data-testid="empty-state">
                <p className="font-display text-xl font-semibold">No hay negocios que coincidan</p>
                <p className="text-slate-500 mt-2 text-sm">Ajusta los filtros para descubrir más oportunidades.</p>
                <Button onClick={clear} className="mt-6 btn-primary" data-testid="empty-clear-btn">Limpiar filtros</Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6 staggered" data-testid="businesses-grid">
                {data.items.map((b) => (
                  <BusinessCard key={b.id} biz={b} onFavorite={toggleFav} isFavorite={favs.includes(b.id)} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
